import { Pin } from "lucide-react";

import { usePermissions } from "@/hooks/usePermissions";
import { usePageTitle } from "@/hooks/usePageTitle";
import { DashboardGrid } from "@/components/dashboard-grid";
import { DASHBOARD_SCOPE } from "@/types/(company)/dashboardLayout.interface";
import { PLATFORM_SECTION_ENUM } from "@/utils/enums";
import { withBranding } from "@/utils/helper";
import { PendingApprovals } from "./_components/PendingApprovals";
import { RecentEmployeesTable } from "./_components/RecentEmployeesTable";
import { useTranslation } from "react-i18next";
import {
  DEFAULT_WORKSPACE_LAYOUT,
  WORKSPACE_WIDGETS,
} from "./_widgets/registry";
import { useWorkspaceRefresh } from "./_widgets/use-workspace-refresh";
import { SetupGuideBanner } from "../company-setup/_components/SetupGuideBanner";
import { QuickActionsBar } from "./_quick-actions/QuickActionsBar";

/**
 * The Workspace dashboard, split in two, under a top bar.
 *
 * **The quick-actions bar.** Shortcuts to the forms this user may submit,
 * permission-filtered and pinned/ordered per user (saved server-side like the
 * canvas, as its own scope).
 *
 * **The widget canvas.** Arrangeable per user: add, remove, reorder and resize.
 * Widgets are compact summaries, permission-filtered per person, and the
 * arrangement is saved server-side so it follows the user across devices. It is
 * as tall as whatever the user put in it.
 *
 * **The detail section, fixed.** The full employee roster and the approvals
 * worklist, in the tabular form a dense table needs and a resizable widget
 * cannot give it. Deliberately not customizable — a table this wide has one good
 * layout. The "Fixed" chip says so out loud, because the obvious question about
 * a partly-customizable page is why the rest has no Customize button.
 *
 * Neither half is pinned to a proportion of the viewport. The page is one
 * scrolling document, which is what stops a full canvas and a full table from
 * fighting over a fixed budget and both losing.
 *
 * The two sections overlap in subject on purpose: the canvas answers "how are we
 * doing", the detail section answers "who exactly, and what needs signing off".
 */
const CompanyDashboard = () => {
  const { t } = useTranslation(["company", "nav"]);
  usePageTitle(withBranding(t("nav:tabs.dashboard")));
  const { can } = usePermissions();
  // Which API slices the widgets draw on is this page's knowledge, not the
  // grid engine's — the engine just renders the control and holds the clock.
  const refreshWidgets = useWorkspaceRefresh();

  const canSeeEmployees = can(PLATFORM_SECTION_ENUM.EMPLOYEES, "view");
  const canSeeApprovals = can(PLATFORM_SECTION_ENUM.TIME_ATTENDANCE, "view");

  return (
    <div className="flex flex-col gap-4">
      <QuickActionsBar />

      {/* Nudge toward the company setup guide. Self-hiding once the workspace
          is live or the banner is dismissed. */}
      <SetupGuideBanner />

      <DashboardGrid
        scope={DASHBOARD_SCOPE.WORKSPACE}
        registry={WORKSPACE_WIDGETS}
        defaultLayout={DEFAULT_WORKSPACE_LAYOUT}
        title={t("company:dashboard.overview")}
        description={t("company:dashboard.press_hold_rearrange")}
        onRefresh={refreshWidgets}
      />

      <section aria-label={t("company:dashboard.employees_and_approvals")} className="flex flex-col">
        <div className="mb-2.5 flex shrink-0 items-center gap-2 px-1">
          <h2 className="shrink-0 text-sm font-semibold text-foreground">
            {t("company:dashboard.details")}
          </h2>
          <span className="flex shrink-0 items-center gap-1 rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold text-muted-foreground">
            <Pin size={9} aria-hidden />
            {t("company:dashboard.fixed")}
          </span>
          <p className="hidden min-w-0 truncate text-xs text-muted-foreground sm:block">
            {t("company:dashboard.always_here_same_place")}
          </p>
        </div>

        {/* No height override: with nothing constraining them from above, these
            fall back to their own 520px, which is what they were built for. */}
        <div className="grid grid-cols-1 gap-3 2xl:grid-cols-3">
          {canSeeEmployees && (
            <div className="2xl:col-span-2">
              <RecentEmployeesTable />
            </div>
          )}
          {canSeeApprovals && (
            <div className="2xl:col-span-1">
              <PendingApprovals />
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default CompanyDashboard;
